import { Alert, Box, Button, Collapse, Skeleton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { PAGE_SIZE } from "../../hooks/use-paged-feed";

import type { PagedFeed } from "../../hooks/use-paged-feed";
import type { ReactElement, ReactNode } from "react";

/**
 * A dashboard feed's rows, a page at a time: placeholders while it loads, a way to try again when
 * it fails, `empty` when there's nothing in it, and Load more while there's more.
 */
export const FeedList = <T extends { id: string }>({
  feed: { items, reloading, error, reload, hasMore, loadingMore, loadMoreError, loadMore, remove },
  renderRow,
  onArchived,
  empty,
  errorMessage,
  analyticsId,
  sliding,
  onSlid,
}: {
  feed: PagedFeed<T>;
  /** A row, which calls `archived` once it's archived. */
  renderRow: (item: T, archived: () => void) => ReactNode;
  /** One was archived here. */
  onArchived: (item: T) => void;
  empty: ReactNode;
  errorMessage: string;
  /** Prefixes the buttons' analytics ids. */
  analyticsId: string;
  /** Those to slide open, rather than just appear. */
  sliding?: ReadonlySet<string>;
  onSlid?: (id: string) => void;
}): ReactElement => {
  // Archived here: collapsed shut, then taken out of the feed.
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const archived = useCallback(
    (item: T) => {
      setLeaving((ids) => new Set(ids).add(item.id));
      onArchived(item);
    },
    [onArchived],
  );
  // Loading more counts what's shown, so it waits until a collapsing row has gone.
  const collapsing = items?.some(({ id }) => leaving.has(id)) ?? false;

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={reload}
            data-analytics-id={`${analyticsId}-retry`}
          >
            Try again
          </Button>
        }
      >
        {errorMessage}
      </Alert>
    );
  }

  if (items === null || (items.length === 0 && hasMore && !loadMoreError)) {
    return (
      <Stack spacing={1.5}>
        {/* As many as the first page, so the rows land where their placeholders were. */}
        {Array.from({ length: PAGE_SIZE }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={58} />
        ))}
      </Stack>
    );
  }

  return (
    // Faded while a change of filter loads, over what was there, rather than placeholders.
    <Box
      aria-busy={reloading}
      sx={(theme) => ({
        opacity: reloading ? 0.6 : 1,
        transition: theme.transitions.create("opacity"),
      })}
    >
      {items.length === 0 && !hasMore ? (
        empty
      ) : (
        <>
          {/* Each row brings its own gap below, so a sliding one opens its gap with it. */}
          <Box component="ul" sx={{ listStyle: "none", m: 0, mb: -1.5, p: 0 }}>
            {items.map((item) => (
              <Collapse
                key={item.id}
                component="li"
                in={!leaving.has(item.id)}
                appear={sliding?.has(item.id) ?? false}
                onEntered={() => onSlid?.(item.id)}
                onExited={() => remove(item.id)}
              >
                <Box sx={{ pb: 1.5 }}>{renderRow(item, () => archived(item))}</Box>
              </Collapse>
            ))}
          </Box>
          {(hasMore || loadMoreError) && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              {loadMoreError && (
                <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                  Couldn't load more.
                </Typography>
              )}
              <Button
                onClick={() => void loadMore()}
                loading={loadingMore}
                disabled={reloading || collapsing}
                data-analytics-id={`${analyticsId}-load-more`}
              >
                Load more
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
